import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useApp } from "../../context/AppState";
import { usePopup } from "../../hooks/usePopup";
import useOfferProducts from "../../hooks/useOfferProducts";
import useEmbeddedUpsellAnalytics from "../../hooks/useEmbeddedUpsellAnalytics";
import { offerTypeUsesRuntimeResolution, PRODUCT_TO_OFFER } from "../../constant/constant";
import { getCartDrawerFlowCss } from "../../utils/sectionBlockCss";
import {
  isPopupRuntimeContentLoading,
  resolveRuntimeProductSlotCount,
} from "../../utils/popup-runtime-content-loading";
import { isSectionVisible } from "../../../shared/section-visibility.js";
import {
  isCartDrawerVerticalShell,
  isCartDrawerCheckboxStackShell,
  isCartDrawerFbtBundleShell,
  resolveCartDrawerUpsellShell,
} from "../../../shared/product-shell-layout.js";
import {
  clampProductPageUpsellItemsPerRow,
  clampProductPageUpsellProductsShownAtOnce,
} from "../../../shared/product-page-upsell-template.js";
import { getProductPageSlotNo } from "../../../shared/product-page-slot.js";
import { isPreviewPlaceholderProduct } from "../../utils/dummyProductGenerator";
import {
  buildFbtCurrentPagePreviewProduct,
  prependCurrentPageProduct,
  resolveCurrentPageProduct,
} from "../../utils/fbt-include-current-page-product";
import {
  anyProductHasSelectableVariants,
  getProductVariants,
  productHasSelectableVariants,
} from "../../layouts/product-recommendation/utils/product-variant-utils";
import cartService from "../../services/cart.service";
import CatalogProductShell from "../Preview/catalog-product/CatalogProductShell";
import CartDrawerUpsellSkeleton from "./CartDrawerUpsellSkeleton";
import { useProductPageUpsellSelection } from "../ProductPageUpsell/ProductPageUpsellSelectionContext";
import { ProductPageUpsellSelectionProvider } from "../ProductPageUpsell/ProductPageUpsellSelectionContext";
import ProductPageFbtClaimButton from "../ProductPageUpsell/ProductPageFbtClaimButton";
import styles from "./cart-drawer-upsell.module.css";

function chunkProducts(list, size) {
  if (!Array.isArray(list) || !list.length) return [];
  const chunkSize = Math.max(1, Number(size) || 1);
  const groups = [];
  for (let index = 0; index < list.length; index += chunkSize) {
    groups.push(list.slice(index, index + chunkSize));
  }
  return groups;
}

const ChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M15 18 9 12l6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="m9 18 6-6-6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Stable placeholder — avoids wiping bundle catalog via a fresh `[]` each render. */
const EMPTY_BUNDLE_PRODUCTS = [];

const GRID_CARD_MIN_PX = 155;
const GRID_CARD_MAX_PX = 340;
const GRID_COLUMN_GAP_PX = 20;

/** How many cards fit in `widthPx` at min size, capped by merchant items-per-row. */
function resolveGridColumnCount(widthPx, itemsPerRow) {
  const preferred = Math.max(1, Math.min(6, Number(itemsPerRow) || 5));
  const width = Number(widthPx);
  if (!Number.isFinite(width) || width <= 0) return preferred;
  const maxFit = Math.max(
    1,
    Math.floor((width + GRID_COLUMN_GAP_PX) / (GRID_CARD_MIN_PX + GRID_COLUMN_GAP_PX))
  );
  return Math.max(1, Math.min(preferred, maxFit));
}

/**
 * Cart drawer upsell strip — shell chrome + shared CatalogProductShell cards.
 */
function CartDrawerUpsellStripBody({
  popupData,
  headingSection,
  productSection,
  activeStep,
  isPreviewMode = true,
  isMobile: isMobileProp,
  embeddedInEditor = false,
  getEditorPartProps,
  headingPartIndex,
  productPartIndex,
  surface = "cart-drawer",
  enableProductPageBundleSelection = false,
}) {
  const { isMobile: viewportIsMobile } = useApp();
  const { isLoadingProducts, fetchRandomProduct } = usePopup();
  const isMobile =
    typeof isMobileProp === "boolean" ? isMobileProp : viewportIsMobile;
  const trackRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [gridColumnCount, setGridColumnCount] = useState(5);
  const [canScrollOverflow, setCanScrollOverflow] = useState(false);

  const shellId = resolveCartDrawerUpsellShell(popupData, productSection);
  const isCarousel = isCartDrawerVerticalShell(shellId);
  const isCheckboxStack = isCartDrawerCheckboxStackShell(shellId);
  const isFbtBundle = isCartDrawerFbtBundleShell(shellId);
  const layoutProfile = isCarousel
    ? "cart-drawer-vertical"
    : isFbtBundle
      ? "cart-drawer-fbt-bundle"
      : isCheckboxStack
        ? "cart-drawer-checkbox-stack"
        : "cart-drawer-horizontal";

  const selection = useProductPageUpsellSelection();
  const lockFirstProductSelection =
    isFbtBundle &&
    productSection?.setting?.layout?.lockFirstProductSelection !== false;
  const [localSelectedByIndex, setLocalSelectedByIndex] = useState(() =>
    lockFirstProductSelection ? { 0: true } : {}
  );
  const showProductCheckbox =
    (isCheckboxStack || isFbtBundle) && surface === "product-page";

  const toggleProductSelection = useCallback(
    (productIndex, nextSelected) => {
      if (
        lockFirstProductSelection &&
        productIndex === 0 &&
        nextSelected === false
      ) {
        return;
      }
      if (selection?.toggleProductSelection) {
        selection.toggleProductSelection(productIndex, nextSelected);
        return;
      }
      setLocalSelectedByIndex((prev) => {
        const isSelected = prev[productIndex] === true;
        const shouldSelect =
          typeof nextSelected === "boolean" ? nextSelected : !isSelected;
        if (!shouldSelect) {
          if (lockFirstProductSelection && productIndex === 0) return prev;
          if (!isSelected) return prev;
          const next = { ...prev };
          delete next[productIndex];
          return next;
        }
        return { ...prev, [productIndex]: true };
      });
    },
    [lockFirstProductSelection, selection]
  );

  const isProductSelected = useCallback(
    (productIndex) => {
      if (selection?.isProductSelected) {
        return selection.isProductSelected(productIndex);
      }
      return localSelectedByIndex[productIndex] === true;
    },
    [localSelectedByIndex, selection]
  );

  const itemsPerRow = useMemo(
    () =>
      surface === "product-page"
        ? clampProductPageUpsellItemsPerRow(productSection?.setting?.layout?.itemsPerRow)
        : 3,
    [productSection, surface]
  );

  const showAsGrid =
    surface === "product-page" &&
    isCarousel &&
    productSection?.setting?.layout?.showAsGrid === true;

  const productsShownAtOnce = useMemo(
    () =>
      surface === "product-page"
        ? clampProductPageUpsellProductsShownAtOnce(
            productSection?.setting?.layout?.productsShownAtOnce
          )
        : 1,
    [productSection, surface]
  );

  const hasVariantPickerSubsection = useMemo(
    () =>
      (productSection?.sections || []).some(
        (sub) => sub?.type === "VARIANT_PICKER" && isSectionVisible(sub)
      ),
    [productSection]
  );

  const { products, isResolvingOfferProducts } = useOfferProducts({
    section: productSection,
    popupData,
    activeStep,
    isPreviewMode,
    isMobile,
    surface,
    fetchOfferProducts: fetchRandomProduct,
    embeddedInEditor,
  });

  const includeCurrentPageProduct =
    isFbtBundle &&
    surface === "product-page" &&
    productSection?.setting?.layout?.includeCurrentPageProduct === true;

  const [currentPageProduct, setCurrentPageProduct] = useState(null);

  useEffect(() => {
    if (!includeCurrentPageProduct) {
      setCurrentPageProduct(null);
      return undefined;
    }

    let cancelled = false;

    if (isPreviewMode || embeddedInEditor) {
      setCurrentPageProduct(buildFbtCurrentPagePreviewProduct());
      return undefined;
    }

    (async () => {
      const product = await resolveCurrentPageProduct();
      if (!cancelled) setCurrentPageProduct(product || null);
    })();

    return () => {
      cancelled = true;
    };
  }, [embeddedInEditor, includeCurrentPageProduct, isPreviewMode]);

  const productPageSlotNo =
    surface === "product-page" ? getProductPageSlotNo(popupData) : null;

  useEmbeddedUpsellAnalytics({
    popupData,
    activeStep,
    enabled: !isPreviewMode && !embeddedInEditor,
    slotNo: productPageSlotNo,
  });

  const [hydratedVariantsByProductId, setHydratedVariantsByProductId] =
    useState({});

  const displayProducts = useMemo(() => {
    const list = products || [];
    const filtered = isPreviewMode
      ? list
      : list.filter((product) => !isPreviewPlaceholderProduct(product));

    // Do NOT strip in-cart products here — that shrinks a pre-limited list
    // (5 → 4 → 3…) without backfill. Hybrid/storefront exclude+fill handles it.

    const withHydratedVariants = filtered.map((product) => {
      const id = product?.id;
      const hydrated =
        id != null ? hydratedVariantsByProductId[String(id)] : null;
      if (!Array.isArray(hydrated) || !hydrated.length) return product;
      if (getProductVariants(product).length > 1) return product;
      return { ...product, variants: hydrated };
    });

    if (!includeCurrentPageProduct) return withHydratedVariants;
    if (!currentPageProduct) return withHydratedVariants;
    return prependCurrentPageProduct(currentPageProduct, withHydratedVariants);
  }, [
    currentPageProduct,
    hydratedVariantsByProductId,
    includeCurrentPageProduct,
    isPreviewMode,
    products,
  ]);

  useLayoutEffect(() => {
    if (!showAsGrid) return undefined;
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === "undefined") {
      setGridColumnCount(resolveGridColumnCount(0, itemsPerRow));
      return undefined;
    }

    const syncColumns = () => {
      setGridColumnCount(resolveGridColumnCount(track.clientWidth, itemsPerRow));
    };
    syncColumns();
    const observer = new ResizeObserver(syncColumns);
    observer.observe(track);
    return () => observer.disconnect();
  }, [showAsGrid, itemsPerRow, displayProducts.length]);

  useEffect(() => {
    if (!lockFirstProductSelection || !isPreviewMode) return;
    setLocalSelectedByIndex((prev) =>
      prev[0] === true ? prev : { ...prev, 0: true }
    );
  }, [displayProducts.length, isPreviewMode, lockFirstProductSelection]);

  useLayoutEffect(() => {
    // Sync before paint so theme ATC intercept never sees an empty catalog
    // after the merchant already checked add-on products.
    if (Array.isArray(displayProducts) && displayProducts.length) {
      selection?.setBundleProducts?.(displayProducts);
    }
  }, [displayProducts, selection]);

  useEffect(() => {
    selection?.setBundleProducts?.(displayProducts);
  }, [displayProducts, selection]);

  const showVariantSelectorSetting =
    productSection?.setting?.showVariantSelector === true;

  // Merchant "Show variant selector" must win — do not hide the row just because
  // the current offer product is single-variant or still hydrating variants.
  // Per-card VariantPicker still no-ops when a product has nothing selectable.
  const showVariantPickerRow = useMemo(() => {
    if (showVariantSelectorSetting) return true;
    return (
      hasVariantPickerSubsection &&
      anyProductHasSelectableVariants(displayProducts)
    );
  }, [
    displayProducts,
    hasVariantPickerSubsection,
    showVariantSelectorSetting,
  ]);

  useEffect(() => {
    if (!showVariantSelectorSetting) return undefined;
    // Admin iframe cannot call shop /products/{handle}.js — catalog products
    // should already include variants. Hydrate only on live storefront.
    if (embeddedInEditor || isPreviewMode) return undefined;

    const targets = (products || []).filter((product) => {
      if (!product?.handle || product?.id == null) return false;
      if (productHasSelectableVariants(product)) return false;
      return !hydratedVariantsByProductId[String(product.id)];
    });
    if (!targets.length) return undefined;

    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(
        targets.map(async (product) => {
          try {
            const full = await cartService.fetchProductByHandle(product.handle);
            const variants = getProductVariants(full);
            if (variants.length > 1) {
              next[String(product.id)] = variants;
            }
          } catch {
            /* storefront may block /products/{handle}.js */
          }
        })
      );
      if (cancelled || !Object.keys(next).length) return;
      setHydratedVariantsByProductId((prev) => ({ ...prev, ...next }));
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally omit hydratedVariantsByProductId from deps — we only
    // enqueue products that are not already cached (checked above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embeddedInEditor, isPreviewMode, products, showVariantSelectorSetting]);

  const productsToOfferType =
    popupData?.generalSettings?.condition?.product?.products_to_offer?.type;
  const usesRuntimeOffer = offerTypeUsesRuntimeResolution(productsToOfferType);

  const showRuntimeSkeleton = useMemo(() => {
    if (embeddedInEditor) return false;

    const runtimeLoading = isPopupRuntimeContentLoading(popupData);
    // Only show skeleton while fetch/hydrate is in flight — empty after resolve
    // must not spin forever (homepage ATC used to leave remcon stuck).
    const awaitingRuntimeProducts =
      usesRuntimeOffer &&
      displayProducts.length === 0 &&
      (isResolvingOfferProducts || runtimeLoading || isLoadingProducts);
    const awaitingSpecificProducts =
      productsToOfferType === PRODUCT_TO_OFFER.SPECIFIC_PRODUCT &&
      isResolvingOfferProducts;

    if (surface === "product-page") {
      if (isPreviewMode && displayProducts.length === 0) {
        return (
          usesRuntimeOffer ||
          productsToOfferType === PRODUCT_TO_OFFER.SPECIFIC_PRODUCT ||
          isLoadingProducts ||
          isResolvingOfferProducts
        );
      }

      return (
        isLoadingProducts ||
        isResolvingOfferProducts ||
        runtimeLoading ||
        awaitingRuntimeProducts ||
        awaitingSpecificProducts
      );
    }

    if (isPreviewMode) {
      return (
        (isLoadingProducts || isResolvingOfferProducts) && displayProducts.length === 0
      );
    }

    return (
      isLoadingProducts ||
      isResolvingOfferProducts ||
      runtimeLoading ||
      awaitingRuntimeProducts ||
      awaitingSpecificProducts
    );
  }, [
    displayProducts.length,
    isLoadingProducts,
    isPreviewMode,
    isResolvingOfferProducts,
    popupData,
    productSection?.runtimeResolved,
    productsToOfferType,
    surface,
    usesRuntimeOffer,
    embeddedInEditor,
  ]);

  const skeletonCount = useMemo(
    () => resolveRuntimeProductSlotCount(popupData, activeStep, productSection),
    [popupData, activeStep, productSection]
  );

  const showHeading =
    headingSection &&
    isSectionVisible(headingSection) &&
    Boolean(String(headingSection?.text || "").trim());
  const titleText = String(headingSection?.text || "").trim();

  const titleStyle = useMemo(
    () => getCartDrawerFlowCss(headingSection, isMobile),
    [headingSection, isMobile]
  );

  const productSlides = useMemo(() => {
    if (!isCheckboxStack) return null;
    return chunkProducts(displayProducts, productsShownAtOnce);
  }, [displayProducts, isCheckboxStack, productsShownAtOnce]);

  const slideCount = isCheckboxStack
    ? productSlides?.length || 0
    : displayProducts.length;

  const stepProduct = useCallback(
    (direction) => {
      setActiveIndex((prev) => {
        const delta = direction === "next" ? 1 : -1;
        const next = Math.max(0, Math.min(slideCount - 1, prev + delta));
        const track = trackRef.current;
        const slide = track?.children?.[next];
        if (slide && typeof slide.scrollIntoView === "function") {
          slide.scrollIntoView({
            behavior: "smooth",
            inline: "start",
            block: "nearest",
          });
        }
        return next;
      });
    },
    [slideCount]
  );

  useEffect(() => {
    setActiveIndex((prev) => Math.min(prev, Math.max(0, slideCount - 1)));
  }, [slideCount]);

  useLayoutEffect(() => {
    if (showAsGrid) {
      setCanScrollOverflow(false);
      return undefined;
    }

    const track = trackRef.current;
    if (!track) {
      setCanScrollOverflow(false);
      return undefined;
    }

    const update = () => {
      setCanScrollOverflow(track.scrollWidth > track.clientWidth + 1);
    };

    update();
    const rafId = requestAnimationFrame(update);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(update)
        : null;
    resizeObserver?.observe(track);

    const mutationObserver =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(update)
        : null;
    mutationObserver?.observe(track, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    window.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [
    displayProducts.length,
    isCarousel,
    isCheckboxStack,
    itemsPerRow,
    productsShownAtOnce,
    showAsGrid,
    slideCount,
    surface,
  ]);

  useEffect(() => {
    if (showAsGrid) return undefined;
    const track = trackRef.current;
    if (!track || slideCount <= 1) return undefined;

    const syncActiveIndexFromScroll = () => {
      const slides = Array.from(track.children);
      if (!slides.length) return;
      const scrollLeft = track.scrollLeft;
      let nearest = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;
      slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - scrollLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = index;
        }
      });
      setActiveIndex((prev) => (prev === nearest ? prev : nearest));
    };

    track.addEventListener("scroll", syncActiveIndexFromScroll, { passive: true });
    return () => track.removeEventListener("scroll", syncActiveIndexFromScroll);
  }, [isCarousel, isCheckboxStack, showAsGrid, slideCount]);

  const shellClassName = `${styles.shell}${
    surface === "product-page" ? ` ${styles.shellProductPage}` : ""
  }${surface === "theme-cart-upsell" ? ` ${styles.shellThemeInject}` : ""}`;

  const stripChromeCss = useMemo(() => {
    if (surface !== "product-page") return {};
    const viewport = isMobile ? "mobile" : "desktop";
    const css =
      popupData?.schema?.[activeStep]?.popupSettings?.customCSS?.[viewport] ||
      {};
    const borderWidth = Number(css.borderWidth) || 0;
    return {
      ...(css.backgroundColor
        ? { backgroundColor: css.backgroundColor }
        : {}),
      ...(css.borderRadius != null && css.borderRadius !== ""
        ? { borderRadius: css.borderRadius }
        : {}),
      ...(borderWidth > 0
        ? {
            borderWidth,
            borderStyle: css.borderStyle || "solid",
            borderColor: css.borderColor || "#e2e8f0",
          }
        : {}),
    };
  }, [activeStep, isMobile, popupData, surface]);

  const shellStyle = {
    ...stripChromeCss,
    ...(surface === "product-page" && isCarousel
      ? {
          "--pp-items-per-row": itemsPerRow,
          "--pp-grid-cols": showAsGrid ? gridColumnCount : itemsPerRow,
          "--pp-card-min": `${GRID_CARD_MIN_PX}px`,
          "--pp-card-max": `${GRID_CARD_MAX_PX}px`,
          "--pp-card-count": showAsGrid
            ? gridColumnCount
            : showRuntimeSkeleton
              ? itemsPerRow
              : Math.min(Math.max(displayProducts.length, 1), itemsPerRow),
        }
      : {}),
  };

  const carouselTrackClassName = `${styles.carouselTrack}${
    showAsGrid ? ` ${styles.carouselTrackGrid}` : ""
  }${
    !showAsGrid &&
    surface === "product-page" &&
    displayProducts.length > 0 &&
    displayProducts.length <= itemsPerRow
      ? ` ${styles.carouselTrackSparse}`
      : ""
  }`;

  useEffect(() => {
    if (isPreviewMode || embeddedInEditor || surface !== "cart-drawer") return;
    const sectionProducts = Array.isArray(productSection?.products)
      ? productSection.products
      : [];
    console.log("[WEU CartUpsell] strip state", {
      popupId: popupData?._id,
      offerType: productsToOfferType,
      usesRuntimeOffer,
      showRuntimeSkeleton,
      displayCount: displayProducts.length,
      rawProductCount: Array.isArray(products) ? products.length : 0,
      isResolvingOfferProducts,
      isLoadingProducts,
      runtimeLoading: isPopupRuntimeContentLoading(popupData),
      runtimeResolved: productSection?.runtimeResolved === true,
      sectionType: productSection?.type,
      sectionPart: productSection?.setting?.cartDrawerPart,
      sectionProductCount: sectionProducts.length,
      sectionProducts: sectionProducts.map((p) => ({
        id: p?.id,
        title: p?.title,
        variants: Array.isArray(p?.variants) ? p.variants.length : 0,
        available: p?.available,
        status: p?.status,
        published_at: p?.published_at ?? p?.publishedAt,
      })),
      displayProducts: displayProducts.map((p) => ({
        id: p?.id,
        title: p?.title,
      })),
      willReturnNull: !showRuntimeSkeleton && displayProducts.length === 0,
    });
  }, [
    isPreviewMode,
    embeddedInEditor,
    surface,
    popupData,
    productsToOfferType,
    usesRuntimeOffer,
    showRuntimeSkeleton,
    displayProducts,
    products,
    isResolvingOfferProducts,
    isLoadingProducts,
    productSection,
  ]);

  if (showRuntimeSkeleton) {
    return (
      <section
        className={shellClassName}
        data-weupsell-surface={surface}
        style={shellStyle}
        aria-label={titleText || "Loading product recommendations"}
        aria-busy="true"
      >
        <CartDrawerUpsellSkeleton
          showHeading={showHeading}
          isCarousel={isCarousel}
          isCheckboxStack={isCheckboxStack}
          showAsGrid={showAsGrid}
          itemsPerRow={itemsPerRow}
          productsShownAtOnce={productsShownAtOnce}
          count={skeletonCount}
          surface={surface}
        />
      </section>
    );
  }

  if (!displayProducts.length) {
    console.warn("[WEU CartUpsell] strip return null — no displayProducts", {
      popupId: popupData?._id,
      offerType: productsToOfferType,
      sectionProductCount: Array.isArray(productSection?.products)
        ? productSection.products.length
        : 0,
      runtimeResolved: productSection?.runtimeResolved === true,
    });
    return null;
  }

  const navControls = (
    <div className={styles.nav}>
      <button
        type="button"
        className={styles.navBtn}
        aria-label="Previous product"
        disabled={activeIndex <= 0}
        onClick={() => stepProduct("prev")}
      >
        <ChevronLeft />
      </button>
      {isCheckboxStack ? (
        <span className={styles.pageIndicator} aria-live="polite">
          {activeIndex + 1} / {slideCount}
        </span>
      ) : null}
      <button
        type="button"
        className={styles.navBtn}
        aria-label="Next product"
        disabled={activeIndex >= slideCount - 1}
        onClick={() => stepProduct("next")}
      >
        <ChevronRight />
      </button>
    </div>
  );

  const cardProps = {
    section: productSection,
    popupData,
    activeStep,
    isMobile,
    isPreviewMode,
    shellId,
    layoutProfile,
    surface,
    hideProductButton: showProductCheckbox,
    showProductCheckbox,
    isFbtBundleCheckbox: isFbtBundle && showProductCheckbox,
    onProductCheckboxChange: toggleProductSelection,
    isProductCheckboxSelected: isProductSelected,
    // Only disable the first checkbox when merchant locked it (Settings).
    isProductCheckboxDisabled:
      lockFirstProductSelection && showProductCheckbox
        ? (productIndex) => productIndex === 0
        : undefined,
  };

  const renderCarousel = () => (
    <>
      {showHeading ? (
        <div
          className={styles.headerRow}
          {...(getEditorPartProps?.(headingPartIndex) || {})}
        >
          <h3 className={styles.titleCarousel} style={titleStyle}>
            {titleText}
          </h3>
          {!showAsGrid && (canScrollOverflow || displayProducts.length > 1)
            ? navControls
            : null}
        </div>
      ) : null}
      <div
        className={carouselTrackClassName}
        ref={trackRef}
        {...(getEditorPartProps?.(productPartIndex) || {})}
      >
        {displayProducts.map((productData, productIndex) => (
          <div key={productData?.id || productIndex} className={styles.carouselCol}>
            <CatalogProductShell
              {...cardProps}
              productData={productData}
              productIndex={productIndex}
              showVariantPickerRow={showVariantPickerRow}
            />
          </div>
        ))}
      </div>
    </>
  );

  const renderHorizontal = () => (
    <>
      {showHeading ? (
        <div
          className={styles.headerRow}
          {...(getEditorPartProps?.(headingPartIndex) || {})}
        >
          <h3 className={styles.title} style={titleStyle}>
            {titleText}
          </h3>
          {canScrollOverflow || displayProducts.length > 1 ? navControls : null}
        </div>
      ) : null}
      <div
        className={styles.horizontalTrack}
        ref={trackRef}
        {...(getEditorPartProps?.(productPartIndex) || {})}
      >
        {displayProducts.map((productData, productIndex) => (
          <div
            key={productData?.id || productIndex}
            className={styles.horizontalSlide}
          >
            <CatalogProductShell
              {...cardProps}
              productData={productData}
              productIndex={productIndex}
              showVariantPickerRow={showVariantPickerRow}
            />
          </div>
        ))}
      </div>
    </>
  );

  const renderCheckboxStack = () => (
    <>
      {showHeading ? (
        <div
          className={styles.headerRow}
          {...(getEditorPartProps?.(headingPartIndex) || {})}
        >
          <h3 className={styles.title} style={titleStyle}>
            {titleText}
          </h3>
          {canScrollOverflow || displayProducts.length > 1 ? navControls : null}
        </div>
      ) : null}
      <div
        className={styles.checkboxStackTrack}
        ref={trackRef}
        {...(getEditorPartProps?.(productPartIndex) || {})}
      >
        {(productSlides || []).map((slideProducts, slideIndex) => (
          <div
            key={`slide-${slideIndex}`}
            className={styles.checkboxStackSlide}
          >
            <div className={styles.checkboxStackColumn}>
              {slideProducts.map((productData) => {
                const productIndex = displayProducts.indexOf(productData);
                return (
                  <CatalogProductShell
                    key={productData?.id || `${slideIndex}-${productIndex}`}
                    {...cardProps}
                    productData={productData}
                    productIndex={productIndex}
                    showVariantPickerRow={showVariantPickerRow}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  const renderFbtBundle = () => (
    <>
      {showHeading ? (
        <div
          className={styles.headerRow}
          {...(getEditorPartProps?.(headingPartIndex) || {})}
        >
          <h3 className={styles.title} style={titleStyle}>
            {titleText}
          </h3>
        </div>
      ) : null}
      <div
        className={styles.fbtStack}
        {...(getEditorPartProps?.(productPartIndex) || {})}
      >
        {displayProducts.map((productData, productIndex) => (
          <React.Fragment key={productData?.id || productIndex}>
            {productIndex === 1 ? (
              <div className={styles.fbtPlus} aria-hidden>
                +
              </div>
            ) : null}
            <CatalogProductShell
              {...cardProps}
              productData={productData}
              productIndex={productIndex}
              showVariantPickerRow={showVariantPickerRow}
            />
          </React.Fragment>
        ))}
      </div>
      {surface === "product-page" ? (
        <ProductPageFbtClaimButton
          popupData={popupData}
          products={displayProducts}
          productSection={productSection}
          isPreviewMode={isPreviewMode}
          embeddedInEditor={embeddedInEditor}
          isMobile={isMobile}
          isProductSelected={isProductSelected}
        />
      ) : null}
    </>
  );

  const content = (
    <section
      className={`${shellClassName}${isFbtBundle ? ` ${styles.shellFbtBundle}` : ""}${
        showAsGrid ? ` ${styles.shellProductPageGrid}` : ""
      }`}
      data-weupsell-surface={surface}
      style={shellStyle}
      aria-label={titleText}
    >
      {isFbtBundle
        ? renderFbtBundle()
        : isCheckboxStack
          ? renderCheckboxStack()
          : isCarousel
            ? renderCarousel()
            : renderHorizontal()}
    </section>
  );

  return content;
}

function CartDrawerUpsellStrip(props) {
  const {
    enableProductPageBundleSelection = false,
    isPreviewMode = true,
    popupData,
    productSection,
  } = props;

  const shellId = resolveCartDrawerUpsellShell(popupData, productSection);
  const isFbtBundle = isCartDrawerFbtBundleShell(shellId);
  const lockFirstProductSelection =
    isFbtBundle &&
    productSection?.setting?.layout?.lockFirstProductSelection !== false;

  if (enableProductPageBundleSelection && !isPreviewMode) {
    return (
      <ProductPageUpsellSelectionProvider
        popupData={popupData}
        productSection={productSection}
        products={EMPTY_BUNDLE_PRODUCTS}
        enabled
        defaultSelectFirst={lockFirstProductSelection}
        lockFirstProductSelection={lockFirstProductSelection}
        // FBT uses Claim Offer for the bundle — do not intercept theme ATC
        // (same as pre-Jul-15 behavior). Product Add-Ons still intercepts.
        interceptThemeAddToCart={!isFbtBundle}
      >
        <CartDrawerUpsellStripBody {...props} />
      </ProductPageUpsellSelectionProvider>
    );
  }

  return <CartDrawerUpsellStripBody {...props} />;
}

export default CartDrawerUpsellStrip;

CartDrawerUpsellStripBody.propTypes = {
  popupData: PropTypes.object,
  headingSection: PropTypes.object,
  productSection: PropTypes.object,
  activeStep: PropTypes.number,
  isPreviewMode: PropTypes.bool,
  isMobile: PropTypes.bool,
  embeddedInEditor: PropTypes.bool,
  getEditorPartProps: PropTypes.func,
  headingPartIndex: PropTypes.number,
  productPartIndex: PropTypes.number,
  surface: PropTypes.oneOf(["cart-drawer", "product-page"]),
  enableProductPageBundleSelection: PropTypes.bool,
};

CartDrawerUpsellStrip.propTypes = CartDrawerUpsellStripBody.propTypes;
